import {
  visit,
  DocumentNode,
  GraphQLObjectType,
  GraphQLInterfaceType,
  getNamedType,
  GraphQLSchema,
  SelectionSetNode,
} from 'graphql'
import { isDeepListType, isGraphQLObjectOrInterfaceType } from './helper'

/**
 * クエリノードを再帰的に訪問し、リスト型のオブジェクト名を additionalTypenamesSet（Setオブジェクト） に追加していく関数
 */
export const visitAndCollectListTypenames = (
  queryNode: DocumentNode | SelectionSetNode,
  parentType: GraphQLObjectType | GraphQLInterfaceType,
  additionalTypenamesSet: Set<string>,
  clientSchema: GraphQLSchema,
) => {
  visit(queryNode, {
    /**
     * フィールドのノードを訪問した際の処理
     */
    Field: node => {
      // フィールド名からフィールドの定義を取得
      const field = parentType.getFields()[node.name.value]
      if (field === undefined) return

      // フィールドの型
      const fieldType = field?.type
      // filedType（GraphQLOutputType）からラッパータイプ（GraphQLNonNull, GraphQLList）を取り除いた型を取得
      const namedType = getNamedType(fieldType)

      // フィールドの型がリスト型の場合はadditionalTypenamesに追加する
      if (isDeepListType(fieldType)) {
        additionalTypenamesSet.add(namedType.name)

        /*
         * クエリリクエストをするときにConnectionでedgeを扱う場合 additionalTypenames にはObject名にEdgeがついた名前（`UserEdge`など）が追加されるが、
         * CRUD操作を行うMutationはConnection型ではなく単体のObject型（`User`）を返すことがほとんどであるため、
         * Mutationのレスポンスの `__typename` を使用したドキュメントキャッシュのキャッシュ破棄の仕組みが行われないというケースが発生する。
         * そのため、Object名に `Edge` がついている場合は、そのObjectの `node` フィールドのObject名もadditionalTypenamesに追加されるようにしている。
         * 例: UserEdgeの場合、UserもadditionalTypenamesに追加する
         */
        if (namedType.name.endsWith('Edge')) {
          const edgeType = clientSchema.getType(namedType.name)
          if (isGraphQLObjectOrInterfaceType(edgeType)) {
            // Edge型のnodeフィールドを取得
            const nodeField = edgeType.getFields()['node']
            // nodeフィールドの型を取得
            const nodeType = getNamedType(nodeField?.type)

            if (nodeType !== undefined) {
              // nodeフィールドの型名（Object名）をadditionalTypenamesに追加
              additionalTypenamesSet.add(nodeType.name)
            }
          }
        }
      }

      // フィールドの型がオブジェクト型またはインターフェース型の場合は再帰的に処理する
      if (isGraphQLObjectOrInterfaceType(namedType) && node.selectionSet) {
        visitAndCollectListTypenames(
          node.selectionSet,
          namedType,
          additionalTypenamesSet,
          clientSchema,
        )
      }
    },
    /**
     * フラグメント定義のノードを訪問した際の処理
     * ```graphql
     * fragment UserFragment on User { name }
     * query getUsers { users { ...UserFragment } }
     * ```
     */
    FragmentDefinition: node => {
      // typeCondition.name.value から型の名前を取得し、それを使って型のオブジェクトを取得
      // 例えば、フラグメントが "User" 型に対するものであれば、typeCondition.name.value は "User" を表し
      // clientSchema.getType("User") で型のオブジェクトを取得します
      const namedType = clientSchema.getType(node.typeCondition.name.value)

      // 型がオブジェクト型またはインターフェース型の場合は再帰的に処理する
      if (isGraphQLObjectOrInterfaceType(namedType)) {
        visitAndCollectListTypenames(
          node.selectionSet,
          namedType,
          additionalTypenamesSet,
          clientSchema,
        )
      }
    },
    /**
     * インラインフラグメントのノードを訪問した際の処理
     * ```graphql
     * query { node(id: "1") { ... on User { name } } }
     * ```
     */
    InlineFragment: node => {
      // typeCondition.name.value から型の名前を取得し、それを使って型のオブジェクトを取得
      // 例えば、インラインフラグメントが "User" 型に対するものであれば、typeCondition.name.value は "User" を表し、
      // clientSchema.getType("User") で型のオブジェクトを取得します
      const typeName = node.typeCondition?.name.value
      if (typeName === undefined) return

      // インラインフラグメントの型名を使ってスキーマから型を取得
      const namedType = clientSchema.getType(typeName)

      // 型がオブジェクト型またはインターフェース型の場合は再帰的に処理する
      if (isGraphQLObjectOrInterfaceType(namedType)) {
        visitAndCollectListTypenames(
          node.selectionSet,
          namedType,
          additionalTypenamesSet,
          clientSchema,
        )
      }
    },
  })
}
